import { listMemoriesAction } from '@/app/actions/memory/list';
import { create } from 'zustand';

export interface MemoryItem {
  id: string;
  content?: string;
  title?: string | null;
  summary?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  type?: string;
  status?: string;
}

interface MemoryStore {
  memories: MemoryItem[];
  loading: boolean;
  lastFetched: Date | null;
  searchTerm: string;
  filteredMemories: MemoryItem[];

  // Actions
  setSearchTerm: (term: string) => void;
  fetchMemories: (projectId?: string, tags?: string[]) => Promise<void>;
  addMemory: (memory: MemoryItem) => void;
  removeMemory: (memoryId: string) => void;
  updateMemory: (memoryId: string, updates: Partial<MemoryItem>) => void;
  clearMemories: () => void;
}

const filterMemories = (
  memories: MemoryItem[],
  searchTerm: string
): MemoryItem[] => {
  if (!searchTerm.trim()) {
    return memories;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  return memories.filter((memory) => {
    // Search in content, title, and summary
    const searchableText = [memory.content, memory.title, memory.summary]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Also search in tags if they exist in metadata
    let tagsText = '';
    if (memory.metadata?.category) {
      try {
        const tags = JSON.parse(memory.metadata.category as string);
        if (Array.isArray(tags)) {
          tagsText = tags.join(' ').toLowerCase();
        }
      } catch {
        // Ignore parsing errors
      }
    }

    return (
      searchableText.includes(lowerSearchTerm) ||
      tagsText.includes(lowerSearchTerm)
    );
  });
};

export const useMemoryStore = create<MemoryStore>()((set, get) => ({
  memories: [],
  loading: false,
  lastFetched: null,
  searchTerm: '',
  filteredMemories: [],

  setSearchTerm: (term: string) => {
    const { memories } = get();
    const filteredMemories = filterMemories(memories, term);
    set({ searchTerm: term, filteredMemories });
  },

  fetchMemories: async (projectId?: string, tags?: string[]) => {
    const { lastFetched } = get();

    // Check if we fetched recently (within 5 minutes) to avoid unnecessary API calls
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (lastFetched && lastFetched > fiveMinutesAgo) {
      console.log('Using cached memories (fetched recently)');
      return;
    }

    set({ loading: true });

    try {
      const response = await listMemoriesAction({
        page: 1,
        limit: 1000, // Get all memories for better client-side search
        projectId,
        tags,
      });

      if ('error' in response) {
        throw new Error(response.error);
      }

      const { searchTerm } = get();
      const memories = response.memories as MemoryItem[];
      const filteredMemories = filterMemories(memories, searchTerm);

      set({
        memories,
        filteredMemories,
        loading: false,
        lastFetched: new Date(),
      });
    } catch (error) {
      console.error('Failed to fetch memories:', error);
      set({ loading: false });
    }
  },

  addMemory: (memory: MemoryItem) => {
    const { memories, searchTerm } = get();
    const updatedMemories = [memory, ...memories];
    const filteredMemories = filterMemories(updatedMemories, searchTerm);

    set({
      memories: updatedMemories,
      filteredMemories,
    });
  },

  removeMemory: (memoryId: string) => {
    const { memories, searchTerm } = get();
    const updatedMemories = memories.filter((m) => m.id !== memoryId);
    const filteredMemories = filterMemories(updatedMemories, searchTerm);

    set({
      memories: updatedMemories,
      filteredMemories,
    });
  },

  updateMemory: (memoryId: string, updates: Partial<MemoryItem>) => {
    const { memories, searchTerm } = get();
    const updatedMemories = memories.map((m) =>
      m.id === memoryId ? { ...m, ...updates } : m
    );
    const filteredMemories = filterMemories(updatedMemories, searchTerm);

    set({
      memories: updatedMemories,
      filteredMemories,
    });
  },

  clearMemories: () => {
    set({
      memories: [],
      filteredMemories: [],
      searchTerm: '',
      lastFetched: null,
    });
  },
}));
